import { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
    Box,
    Grid,
    Paper,
    Typography,
    TextField,
    Button,
    Avatar,
    CircularProgress,
    Snackbar,
    Alert,
    IconButton,
    Chip,
    Tooltip,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import SaveIcon from "@mui/icons-material/Save";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { useAuth } from "../../../auth";
import { useEditUser } from "../../../api/bpm/endpoints";

export const Route = createFileRoute("/__auth/profile/")({
    component: ProfileComponent,
    staticData: {
        title: "routes.myProfile",
    },
});

function ProfileComponent() {
    const { t } = useTranslation();
    const { user, userTenant, currentTenant } = useAuth();
    const navigate = useNavigate();

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error";
    }>({ open: false, message: "", severity: "success" });

    const editMutation = useEditUser();

    useEffect(() => {
        if (user) {
            setName(user.name || "");
            setEmail(user.email || "");
        }
    }, [user]);

    const handleSave = () => {
        if (!user?.id) return;

        editMutation.mutate(
            {
                tenant: userTenant || currentTenant,
                userId: user.id,
                data: {
                    name,
                    email,
                    cpf: user.cpf || undefined,
                    matriculaInstitucional: user.matriculaInstitucional || undefined,
                    dataNascimento: user.dataNascimento || undefined,
                    occupation: user.occupation?.id ? { id: user.occupation.id } : undefined,
                    job: user.job?.id ? { id: user.job.id } : undefined,
                    accessProfiles: user.accessProfiles
                        ?.filter((p) => p.id != null)
                        .map((p) => ({
                            id: p.id!,
                            isUnderteminedTime: true,
                            startTime: undefined,
                            endTime: undefined,
                        })),
                    groups: user.groups
                        ?.filter((g) => g.id != null)
                        .map((g) => ({
                            id: g.id!,
                            isUnderteminedTime: true,
                            startTime: undefined,
                            endTime: undefined,
                        })),
                    stockingAreas: user.stockingAreas
                        ?.filter((s) => s.id != null)
                        .map((s) => ({ id: s.id! })),
                },
            },
            {
                onSuccess: () => {
                    setSnackbar({
                        open: true,
                        message: t("profile.saveSuccess"),
                        severity: "success",
                    });
                },
                onError: () => {
                    setSnackbar({
                        open: true,
                        message: t("profile.saveError"),
                        severity: "error",
                    });
                },
            }
        );
    };

    const handleCancel = () => {
        navigate({ to: "/" });
    };

    if (!user) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    const userInitials = user.name
        ? user.name
              .split(" ")
              .map((n) => n[0])
              .slice(0, 2)
              .join("")
              .toUpperCase()
        : "";

    return (
        <Box sx={{ maxWidth: 900, mx: "auto", py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Box sx={{ position: "relative" }}>
                    <Avatar
                        sx={{
                            width: { xs: 100, md: 150 },
                            height: { xs: 100, md: 150 },
                            bgcolor: "primary.main",
                            fontSize: { xs: 32, md: 48 },
                        }}
                    >
                        {userInitials || <PersonIcon sx={{ fontSize: { xs: 40, md: 60 } }} />}
                    </Avatar>
                    <IconButton
                        size="small"
                        sx={{
                            position: "absolute",
                            bottom: 4,
                            right: 4,
                            bgcolor: "background.paper",
                            border: "1px solid",
                            borderColor: "divider",
                            "&:hover": { bgcolor: "action.hover" },
                        }}
                    >
                        <PhotoCameraIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                {t("profile.personalData")}
            </Typography>

            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, md: 3 },
                    mb: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                }}
            >
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <TextField
                            id="profile-id"
                            label={t("profile.identification")}
                            value={user.id || ""}
                            fullWidth
                            size="small"
                            disabled
                            variant="outlined"
                        />
                    </Grid>

                    <Grid size={12}>
                        <TextField
                            id="profile-name"
                            label={t("profile.name")}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth
                            size="small"
                            variant="outlined"
                            inputProps={{ maxLength: 300 }}
                        />
                    </Grid>

                    <Grid size={12}>
                        <TextField
                            id="profile-email"
                            label={t("profile.email")}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            fullWidth
                            size="small"
                            variant="outlined"
                            type="email"
                            inputProps={{ maxLength: 300 }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            id="profile-cpf"
                            label={t("profile.cpf")}
                            value={user.cpf || ""}
                            fullWidth
                            size="small"
                            disabled
                            variant="outlined"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            id="profile-matricula"
                            label={t("profile.matricula")}
                            value={user.matriculaInstitucional || ""}
                            fullWidth
                            size="small"
                            disabled
                            variant="outlined"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            id="profile-birthdate"
                            label={t("profile.birthDate")}
                            value={user.dataNascimento || ""}
                            fullWidth
                            size="small"
                            disabled
                            variant="outlined"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                {t("profile.institutionalBond")}
            </Typography>

            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, md: 3 },
                    mb: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                }}
            >
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            id="profile-stocking-area"
                            label={t("profile.stockingArea")}
                            value={
                                user.stockingAreas && user.stockingAreas.length > 0
                                    ? user.stockingAreas[0].name || ""
                                    : ""
                            }
                            fullWidth
                            size="small"
                            disabled
                            variant="outlined"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            id="profile-job"
                            label={t("profile.jobTitle")}
                            value={user.job?.name || ""}
                            fullWidth
                            size="small"
                            disabled
                            variant="outlined"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            id="profile-occupation"
                            label={t("profile.occupation")}
                            value={user.occupation?.name || ""}
                            fullWidth
                            size="small"
                            disabled
                            variant="outlined"
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                {t("profile.accessProfiles")}
            </Typography>

            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, md: 3 },
                    mb: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    minHeight: 48,
                }}
            >
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {user.accessProfiles && user.accessProfiles.length > 0 ? (
                        user.accessProfiles.map((profile) => (
                            <Chip
                                key={profile.id}
                                label={profile.name}
                                size="small"
                                variant="outlined"
                                color="primary"
                            />
                        ))
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            —
                        </Typography>
                    )}
                </Box>
            </Paper>

            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                {t("profile.groups")}
            </Typography>

            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, md: 3 },
                    mb: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    minHeight: 48,
                }}
            >
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {user.groups && user.groups.length > 0 ? (
                        user.groups.map((group) => {
                            const groupTenant = group.id ? group.id.substring(group.id.lastIndexOf("_") + 1) : undefined;
                            const isSameTenant = !userTenant || !groupTenant || groupTenant === userTenant;

                            const chipElement = (
                                <Chip
                                    key={group.id}
                                    label={group.name}
                                    size="small"
                                    variant="outlined"
                                    color={isSameTenant ? "primary" : "secondary"}
                                    sx={
                                        !isSameTenant
                                            ? {
                                                  borderColor: "warning.main",
                                                  color: "warning.dark",
                                                  bgcolor: "action.hover",
                                              }
                                            : undefined
                                    }
                                />
                            );

                            if (!isSameTenant && groupTenant) {
                                return (
                                    <Tooltip
                                        key={group.id}
                                        title={t("profile.groupBelongsToOrganization", { tenant: groupTenant })}
                                        arrow
                                    >
                                        <span>{chipElement}</span>
                                    </Tooltip>
                                );
                            }

                            return chipElement;
                        })
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            —
                        </Typography>
                    )}
                </Box>
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
                <Button
                    id="profile-cancel-btn"
                    variant="outlined"
                    onClick={handleCancel}
                >
                    {t("profile.cancel")}
                </Button>
                <Button
                    id="profile-save-btn"
                    variant="contained"
                    startIcon={
                        editMutation.isPending ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : (
                            <SaveIcon />
                        )
                    }
                    onClick={handleSave}
                    disabled={editMutation.isPending || !name || !email}
                >
                    {t("profile.save")}
                </Button>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
